import {
  Flex,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
} from '@chakra-ui/react';
import React, { useState, useEffect, useCallback } from 'react';

import { csvConversion, finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import {
  useGetAssignedReceivablesForLoanProductLazyQueryGQLType,
  AssignmentConfirmationJobGQLType,
  useInitiateReceivableReassignmentFromDataPipelineMutation,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useReceivableReconciliationAlertContext } from './ReceivableReconciliationAlertContext';
import { ReceivableReconciliationTable } from './ReceivableReconciliationTable';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useGetLatestMeshRunQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { CustomError } from 'packages/dataPipeline/types/dataPipelineTypes';
import { usePollRepurchaseAndTransferJobs } from 'packages/loanProduct/hooks/usePollRepurchaseAndTransferJobs';
import { useSlugToName } from '~/packages/dataPipeline/hooks/slugToNameContext';
import { useUploadContext } from '~/packages/dataPipeline/hooks/UploadContext';

export const determineLoanTapeErrorState = (
  artifacts: DataPipeline.Artifact[],
  instances: DataPipeline.Instance[],
): {
  offendingExternalReceivableIds: string[] | null;
  hasOmittedRows: boolean;
} => {
  const masterLoanTapeArtifact = artifacts.find(
    (artifact) => artifact.type === 'masterLoanTape',
  );
  if (masterLoanTapeArtifact) {
    const erroredMasterLoanTapeInstance = instances.find(
      (instance) =>
        instance.artifactId === masterLoanTapeArtifact.id &&
        instance.status === 'error',
    );
    if (erroredMasterLoanTapeInstance) {
      // this assert will help TS narrow down the type.
      finleyAssert(
        erroredMasterLoanTapeInstance.status === 'error',
        `Was expecting error status. [Found:${erroredMasterLoanTapeInstance.status}]`,
      );
      const {
        statusDetails: { errors },
      } = erroredMasterLoanTapeInstance;

      // check if instance failed due to receivable assignment error
      const assignmentErrors = errors.filter((error) =>
        error.content.externalMessage.startsWith(
          'Receivables already assigned',
        ),
      );

      if (assignmentErrors.length > 0) {
        const offendingExternalReceivableIds: string[] = [];
        let hasOmittedRows = false;

        assignmentErrors.forEach((assignmentError) => {
          const {
            content: { tableData },
          } = assignmentError;
          hasOmittedRows = hasOmittedRows || !!tableData?.omittedRows;
          const receivableErrorData = tableData
            ? csvConversion.csvStringToJsonArray({
                csvString: tableData.csvString,
              }).data
            : null;

          finleyAssert(
            receivableErrorData,
            'Expected to find receivable error data for errored master loan tape instance',
          );

          receivableErrorData.forEach((receivableError) => {
            offendingExternalReceivableIds.push(
              receivableError['Attempted Receivable ID'],
            );
          });
        });

        return { offendingExternalReceivableIds, hasOmittedRows };
      }
    }
  }
  return { offendingExternalReceivableIds: null, hasOmittedRows: false };
};

/**
 * returns a custom error message if the error is about receivable assignment
 */
export const getCustomError = (
  artifacts: DataPipeline.Artifact[],
  instances: DataPipeline.Instance[],
): CustomError | null => {
  const {
    offendingExternalReceivableIds,
    hasOmittedRows,
  } = determineLoanTapeErrorState(artifacts, instances);
  if (offendingExternalReceivableIds !== null) {
    const customMessage = `There are discrepancies in the pipeline — the loan tape does not align with Finley's receivable records. ${
      hasOmittedRows ? ' Contact Finley Support for assistance.' : ''
    }`;
    return { customMessage };
  }
  return null;
};

export const ReceivableReconciliation = ({
  implementationId,
  loanProductSlug,
  creditFacilitySlug,
}: {
  implementationId: string;
  loanProductSlug: string;
  creditFacilitySlug: string;
}) => {
  const { cfSlugToName } = useSlugToName();

  const {
    showErrorAlert,
    showInProgressAlert,
    showSuccessAlert,
  } = useReceivableReconciliationAlertContext();

  const {
    data: latestMeshRunData,
    loading: latestMeshRunLoading,
  } = useGetLatestMeshRunQuery({
    fetchPolicy: 'no-cache',
    variables: {
      implementationId,
    },
  });

  const { completeRecord, instances } = useUploadContext();

  const [externalReceivableIds, setExternalReceivableIds] = useState<
    string[]
  >();

  const [
    getReceivablesData,
    {
      data: receivablesData,
      loading: areReceivablesLoading,
      error: getReceivablesError,
    },
  ] = useGetAssignedReceivablesForLoanProductLazyQueryGQLType({
    fetchPolicy: 'no-cache',
  });

  const refetchReceivables = useCallback(() => {
    if (externalReceivableIds) {
      getReceivablesData({
        variables: { loanProductSlug, externalReceivableIds },
      });
    }
  }, [externalReceivableIds, getReceivablesData, loanProductSlug]);

  useEffect(() => {
    if (!completeRecord || latestMeshRunLoading || !latestMeshRunData) {
      return;
    }

    const latestMeshRunRecord = latestMeshRunData.getLatestMeshRun
      ? DbGraphUtils.deserializeMeshRunRecord(
          latestMeshRunData.getLatestMeshRun,
        )
      : null;

    const {
      meshRunRecord: { status, reportingDate, fileVersion },
      meshDocument: { artifacts },
    } = completeRecord;

    if (status !== 'error') {
      return;
    }

    // mesh run must be of latest reportingDate and fileVersion
    if (
      latestMeshRunRecord &&
      (latestMeshRunRecord.reportingDate !== reportingDate ||
        latestMeshRunRecord.fileVersion !== fileVersion)
    ) {
      return;
    }

    const {
      offendingExternalReceivableIds,
      hasOmittedRows,
    } = determineLoanTapeErrorState(artifacts, instances);

    if (offendingExternalReceivableIds !== null && !hasOmittedRows) {
      setExternalReceivableIds(offendingExternalReceivableIds);
    }
  }, [completeRecord, instances, latestMeshRunData, latestMeshRunLoading]);

  useEffect(() => {
    if (externalReceivableIds) {
      getReceivablesData({
        variables: { loanProductSlug, externalReceivableIds },
      });
    }
  }, [externalReceivableIds, getReceivablesData, loanProductSlug]);

  const { repurchaseAndTransferJobs } = usePollRepurchaseAndTransferJobs({
    implementationId,
  });

  const [
    inProgressJob,
    setInProgressJob,
  ] = useState<AssignmentConfirmationJobGQLType | null>(null);

  // initiatedJobInfo is used to display alert when a job is first initiated, before jobs are polled next
  const [initiatedJobInfo, setInitiatedJobInfo] = useState<{
    id: string;
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
    receivableCount: number;
  } | null>(null);

  const [
    terminalJob,
    setTerminalJob,
  ] = useState<AssignmentConfirmationJobGQLType | null>(null);

  const [
    receivableCountForConfirmationJob,
    setReceivableCountForConfirmationJob,
  ] = useState<number | null>(null);

  useEffect(() => {
    if (initiatedJobInfo) {
      setTerminalJob(null);
      setReceivableCountForConfirmationJob(initiatedJobInfo.receivableCount);
    }
  }, [initiatedJobInfo]);

  useEffect(() => {
    const inProgressJobs = repurchaseAndTransferJobs.filter(
      (job) => job.status === 'inProgress',
    );
    finleyAssert(
      inProgressJobs.length <= 1,
      `Expected maximum one repurchase or transfer confirmation job to be in progress for [implementationId ${implementationId}]`,
    );
    let terminatedJob: AssignmentConfirmationJobGQLType | null = null;
    if (inProgressJobs.length === 1) {
      // a job is in progress
      setInProgressJob(inProgressJobs[0]);
      setInitiatedJobInfo(null);
      setTerminalJob(null);
    } else if (inProgressJob) {
      // a job was in progress and should now be in terminal status
      const jobId = inProgressJob.id;
      const jobRecord = repurchaseAndTransferJobs.find(
        (job) => job.id === jobId,
      );
      finleyAssert(jobRecord, `Expected to find job with id ${jobId}`);
      terminatedJob = jobRecord;
    } else if (initiatedJobInfo) {
      // job was kicked off in UI, but either we polled and the job record doesn't exist yet
      // or the job completed in between polls
      const jobId = initiatedJobInfo.id;
      const jobRecord = repurchaseAndTransferJobs.find(
        (job) => job.id === jobId,
      );
      terminatedJob = jobRecord ?? null;
    }
    if (terminatedJob) {
      setTerminalJob(terminatedJob);
      setInProgressJob(null);
      setInitiatedJobInfo(null);
      if (terminatedJob.status === 'success') {
        refetchReceivables();
      }
    }
  }, [
    implementationId,
    inProgressJob,
    initiatedJobInfo,
    refetchReceivables,
    repurchaseAndTransferJobs,
  ]);

  const [
    initiateReceivableReassignmentFromDataPipeline,
    { error: initiateReceivableReassignmentError },
  ] = useInitiateReceivableReassignmentFromDataPipelineMutation({
    onCompleted: (data) => {
      if (initiatedJobInfo) {
        setInitiatedJobInfo({
          id: data.initiateReceivableReassignmentFromDataPipeline.jobId,
          jobType: initiatedJobInfo.jobType,
          receivableCount:
            data.initiateReceivableReassignmentFromDataPipeline.receivableCount,
        });
      }
    },
    onError: () => {
      setInProgressJob(null);
    },
  });

  const initiateReassignmentConfirmationJob = ({
    rowSelectionData,
    jobType,
  }: {
    rowSelectionData: any;
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
  }) => {
    // set dummy initial job info to display alert
    // then set actual initiated job info after it's kicked off
    setInitiatedJobInfo({
      id: 'job-id',
      jobType,
      receivableCount: 0,
    });

    initiateReceivableReassignmentFromDataPipeline({
      variables: {
        loanProductSlug,
        implementationId,
        externalReceivableIds: Object.keys(rowSelectionData),
        creditFacilitySlug:
          jobType === 'repurchase' ? null : creditFacilitySlug,
      },
    });
  };

  useEffect(() => {
    if (
      !initiateReceivableReassignmentError &&
      (initiatedJobInfo || inProgressJob)
    ) {
      const jobType = initiatedJobInfo?.jobType || inProgressJob?.jobType;
      if (jobType) {
        showInProgressAlert(jobType);
      }
    }
  }, [
    initiatedJobInfo,
    inProgressJob,
    showInProgressAlert,
    initiateReceivableReassignmentError,
  ]);

  const misAssignedReceivables = receivablesData?.assignedReceivablesForLoanProduct.filter(
    (receivable) => receivable.creditFacility.slug !== creditFacilitySlug,
  );

  const allReceivablesReconciled = !!(
    misAssignedReceivables && misAssignedReceivables.length === 0
  );

  useEffect(() => {
    if (
      !initiatedJobInfo &&
      !inProgressJob &&
      terminalJob &&
      terminalJob.status === 'success' &&
      receivableCountForConfirmationJob !== null
    ) {
      showSuccessAlert(
        terminalJob.jobType,
        receivableCountForConfirmationJob,
        allReceivablesReconciled,
        terminalJob.jobType === 'transfer'
          ? cfSlugToName(creditFacilitySlug)
          : null,
      );
    }
  }, [
    initiatedJobInfo,
    inProgressJob,
    terminalJob,
    receivableCountForConfirmationJob,
    allReceivablesReconciled,
    showSuccessAlert,
    cfSlugToName,
    creditFacilitySlug,
  ]);

  useEffect(() => {
    if (initiateReceivableReassignmentError && initiatedJobInfo) {
      showErrorAlert(initiatedJobInfo.jobType);
      refetchReceivables();
    } else if (
      !initiatedJobInfo &&
      !inProgressJob &&
      terminalJob &&
      terminalJob.status === 'error'
    ) {
      showErrorAlert(terminalJob.jobType);
      refetchReceivables();
    }
  }, [
    initiatedJobInfo,
    inProgressJob,
    terminalJob,
    receivableCountForConfirmationJob,
    allReceivablesReconciled,
    initiateReceivableReassignmentError,
    refetchReceivables,
    showErrorAlert,
  ]);

  if (!externalReceivableIds || !latestMeshRunData || latestMeshRunLoading) {
    return null;
  }

  return (
    <Flex direction="column">
      {(areReceivablesLoading ||
        getReceivablesError ||
        !allReceivablesReconciled) && (
        <>
          <Typography variant="base-muted" TextProps={{ mt: '24px' }}>
            The Receivable IDs are already assigned to a facility that differs
            from the one specified on the loan tape. Select the receivables you
            would like to repurchase onto the balance sheet.
          </Typography>
          <Box mt="24px">
            <>
              {areReceivablesLoading && <Skeleton mb="24px" height="50vh" />}
              {!areReceivablesLoading &&
                misAssignedReceivables &&
                !getReceivablesError && (
                  <ReceivableReconciliationTable
                    receivables={misAssignedReceivables}
                    creditFacilityName={cfSlugToName(creditFacilitySlug)}
                    initiateReassignmentConfirmationJob={
                      initiateReassignmentConfirmationJob
                    }
                    jobInProgress={
                      !!inProgressJob ||
                      (!initiateReceivableReassignmentError &&
                        !!initiatedJobInfo)
                    }
                  />
                )}

              {getReceivablesError && (
                <Alert status="error" mb="32px">
                  <Flex gap={1}>
                    <AlertIcon />
                    <AlertDescription>
                      An unexpected error occurred when fetching invalid
                      receivables for loan tape. Please contact Finley for
                      support.
                    </AlertDescription>
                  </Flex>
                </Alert>
              )}
            </>
          </Box>
        </>
      )}
    </Flex>
  );
};
